/**
 * The Co-Living occupancy matrix's view model (Slice 4, corrected in S4.1).
 *
 * NOTHING HERE DECIDES A LIFECYCLE STATE, AND NOTHING HERE DECIDES WHICH
 * TENANCY A ROOM READS. Both questions now belong to
 * `utils/lifecycleCalendarTruth`, which the shared `components/RoomCalendar`
 * imports as well — so the two calendars in this product cannot drift apart
 * again. Before S4.1 each of them owned a private copy, and both copies were
 * wrong in the same way: a cancelled tenancy could out-rank the live one and
 * paint an occupied room free.
 *
 * WHAT IS LEFT HERE IS SHAPE, NOT MEANING: grouping rooms under units, the
 * one-line room context, and rolling the per-room figures up per unit. The
 * month axis, the per-month truth and the counting rules are imported.
 */
import { isActiveRoom } from "../../../utils/unitOccupancyStatus";
import {
  DEFAULT_MONTH_COUNT,
  UNKNOWN_STATE,
  buildCalendarAggregates,
  buildRoomCalendarCells,
  resolveMonthAxis,
  sumCalendarAggregates,
} from "../../../utils/lifecycleCalendarTruth";

export {
  DATA_QUALITY_NOTICE,
  DEFAULT_MONTH_COUNT,
  FAMILY_LABELS,
  INCOMPLETE_HINT,
  INCOMPLETE_VALUE_LABEL,
  LEGEND_GROUPS,
  LIFECYCLE_FAMILY,
  LIFECYCLE_LABELS,
  LIFECYCLE_STATES,
  MAX_MONTH_COUNT,
  MONTH_COUNT_OPTIONS,
  UNKNOWN_STATE,
} from "../../../utils/lifecycleCalendarTruth";

function roomMinStayMonths(room) {
  const v = room.default_minimum_stay_months ?? room.defaultMinimumStayMonths;
  const n = Number(v);
  return Number.isFinite(n) ? n : 3;
}

function roomNoticeMonths(room) {
  const v = room.default_notice_period_months ?? room.defaultNoticePeriodMonths;
  const n = Number(v);
  return Number.isFinite(n) ? n : 1;
}

/**
 * The room's one-line context, in the order the old calendar printed it:
 * current status · rent · move-in · minimum stay · notice period.
 *
 * ACTIVITY COMES FIRST IN THE LEADING SEGMENT. The label handed in is the
 * room's occupancy, and for a stood-down room that label is a lie by
 * construction: a retired room has no tenancy, so it classifies as "frei" and
 * the row announced it as available capacity. `isActiveRoom` is the same
 * activity truth the room table, the room-status overview, the room map and
 * the room detail page use — never `room.status`, which the wire fills with
 * "Frei" for a retired room.
 *
 * ONLY THE LEADING SEGMENT MOVES. Rent, move-in, minimum stay and notice
 * period are facts about the room and stay exactly as they were.
 */
export function buildRoomMeta(room, occupancyLabel) {
  const parts = [isActiveRoom(room) ? occupancyLabel || "—" : "Inaktiv"];
  if (room.priceMonthly) {
    parts.push(`CHF ${Number(room.priceMonthly).toLocaleString("de-CH")}`);
  }
  if (room.moveInDate) parts.push(`Einzug ${room.moveInDate}`);
  parts.push(`Mindestdauer ${roomMinStayMonths(room)}M`);
  parts.push(`Kündigung ${roomNoticeMonths(room)}M`);
  return parts.join(" · ");
}

/**
 * The whole matrix: month columns, then one group per unit with its room rows.
 *
 * `groups` arrives already filtered and already ordered by the page — this does
 * not sort, because the order on screen has to match the unit section above it.
 *
 * The returned `dataQuality` is what the header uses to decide whether to say
 * anything about incomplete data. It reports rather than hides: the matrix
 * stays fully visible, and the unknown cells say so themselves.
 */
export function buildOccupancyCalendarModel({
  groups = [],
  tenancies = null,
  monthCount = DEFAULT_MONTH_COUNT,
  now = undefined,
  occupancyLabelFor = () => "—",
}) {
  const axis = resolveMonthAxis({ tenancies, monthCount, now });
  const months = axis.months;

  const modelGroups = groups.map((group) => {
    const rooms = (group.rooms || []).map((room, index) => {
      const cells = buildRoomCalendarCells({ room, tenancies, months });
      return {
        key: String(room.roomId ?? room.id ?? index),
        name: String(room.roomName || room.name || `Zimmer ${index + 1}`),
        /**
         * CURRENT INVENTORY STATE — NOT A MONTH STATE.
         *
         * `is_active` is a bare boolean on `Room` with no companion
         * timestamp anywhere in the schema, so it says whether the room is
         * in service NOW and nothing about any particular month. It
         * therefore reaches the ROW and must never reach a cell: `cells`
         * and `aggregates` below are built without it, exactly as before.
         *
         * Carried here because the model's room objects are all the
         * calendar components see — the page documents that `is_active`
         * used not to be among them, which is why the matrix could render
         * a retired room as ordinary inventory.
         */
        isActive: isActiveRoom(room),
        meta: buildRoomMeta(room, occupancyLabelFor(room)),
        cells,
        aggregates: buildCalendarAggregates(cells, room.priceMonthly),
      };
    });
    return {
      unitId: group.unitId,
      displayId: group.displayId,
      addressLine1: group.addressLine1,
      addressLine2: group.addressLine2,
      roomCount: rooms.length,
      rooms,
      aggregates: sumCalendarAggregates(rooms.map((r) => r.aggregates)),
    };
  });

  const unknownCells = modelGroups.reduce(
    (n, g) =>
      n +
      g.rooms.reduce(
        (m, r) => m + r.cells.filter((c) => c.state === UNKNOWN_STATE).length,
        0
      ),
    0
  );

  return {
    months,
    groups: modelGroups,
    dataQuality: {
      unknownCells,
      hasUnknown: unknownCells > 0,
      axisSource: axis.source,
      axisConsistent: axis.consistent,
      axisAnchors: axis.anchors,
    },
  };
}
